import { Instance, SnapshotOut, types } from "mobx-state-tree"
import { BaseModelStoreProps } from "../base-model-store/base-model-store"
import { withEnvironment } from "../extensions/with-environment"
import { withRootStore } from "../extensions/with-root-store"
import { withBaseModel } from "../extensions/with-base-model"
import _ from "lodash"
import { createAxios } from "../../utils/axios"
import moment from "moment"

const ChemicalPredictionsStoreProps = {
  ...BaseModelStoreProps,
  listParameterPredictions: types.optional(types.array(types.frozen()),[]),
  listSyncActualPredictions: types.optional(types.array(types.frozen()),[]),
  data: types.optional(types.array(types.frozen()),[]),
  currentPage: types.optional(types.number, 1),
  nextPage: types.optional(types.number, 1),
  lastPage: types.optional(types.number, 0),
}

/**
 * Model description here for TypeScript hints.
 */
export const ChemicalPredictionsStoreModel = types
  .model("ChemicalPredictionsStore")
  .props({})
  .props(ChemicalPredictionsStoreProps)
  .extend(withEnvironment)
  .extend(withRootStore)
  .extend(withBaseModel)
  .views((self) => ({})) // eslint-disable-line @typescript-eslint/no-unused-vars
  .actions((self) => ({
    set(key: keyof typeof ChemicalPredictionsStoreProps, value: any) {
      if (self[key] === value) return
      self[key] = value as never;
    },
    init(){
      self.initModel();
    },
    reset() {
      this.set("data", []);
    },
    async getChemicalPredictions(cycleId: number, predictionDates: string, page: number) {
      this.set('loading', true);
      this.set('currentPage', page || 1)

      const models = await createAxios().get('measurement_predictions', {
        params: {
          page: page,
          per_page: 1000,
          cycle_id: cycleId,
          prediction_dates: predictionDates,
        }
      }).catch((e) => {
        console.warn(e)
        this.set('error', e);
        this.set('loading', false)
        return []
      }).then((response: any) => {
        const dataResponse = response?.data || {};
        const emptyPredictions = []
        let lastDateItem = null
        const groupedData = _(dataResponse?.data || []).groupBy('logged_date_tz').map((predictions, date) => ({
          date: date,
          age: predictions.length ? predictions[0].age : 0,
          predictions: predictions
        })).value()
        .sort((a, b) => moment(a.date).isBefore(moment(b.date)) ? -1 : 0)
        .map((value) => {
          if (lastDateItem && moment.duration(moment(value.date).diff(moment(lastDateItem.date))).asDays() > 1) {
            const startDate = moment(lastDateItem.date).add(1, "days")
            const endDate = moment(value.date)
            let index = 1
            while(startDate.isBefore(endDate)) {
              emptyPredictions.push({
                date: startDate.format("YYYY-MM-DD"),
                age: lastDateItem.age + index,
                predictions: []
              })
              startDate.add(1, "days");
              index++;
            }
          }
          lastDateItem = value
          return value;
        })
        this.set('lastPage', dataResponse?.meta?.last_page || 0)
        if (dataResponse?.meta?.current_page + 1 > dataResponse?.meta?.last_page) {
          this.set('loading', false)
          this.set('nextPage', 0)
        } else {
          this.set('nextPage', (dataResponse?.meta?.current_page || 0) + 1)
        }
        const mergedOld = _.merge(_.keyBy(self.data, 'date'), _.keyBy([...groupedData, ...emptyPredictions], 'date'));
        const values = _.values(mergedOld);
        return values
      })

      this.set("data", models)
      return models
    },
    getValuePredictions(listData?: Array<any>, parameterId?: number) {
      return listData ? listData.find(item => item.parameter_id === parameterId) || {} : {}
    },
    async getListParametersPredictions(cycle: any, listActualChemical: Array<any>) {
      if (!self.data.length) {
        return []
      }
      const currentPrediction = self.data.find(value => value.date === moment(cycle?.finished_at || new Date()).format("YYYY-MM-DD")) || {};

      const lastActualChemical = listActualChemical.length ? listActualChemical.sort((a, b) => b.age - a.age)[0] : {}

      const age = currentPrediction.age
      const isEmpty = !currentPrediction.age

      const models = [
        {
          title: "chemicalParameterScreen.alkalinity",
          age: age,
          latestActualAge: lastActualChemical?.age || 0,
          isEmpty: isEmpty,
          nameTranslated: "carbonat",
          data: [
            {
              key: "carbonat",
              value: parseFloat(this.getValuePredictions(currentPrediction.predictions, 18).value || 0),
              actualValue: parseFloat(lastActualChemical?.carbonat || 0)
            },
            {
              key: "bicarbonat",
              value: parseFloat(this.getValuePredictions(currentPrediction.predictions, 19).value || 0),
              actualValue: parseFloat(lastActualChemical?.bicarbonat || 0)
            },
            {
              key: "alkalinity",
              value: parseFloat(this.getValuePredictions(currentPrediction.predictions, 14).value || 0),
              actualValue: parseFloat(lastActualChemical?.alkalinity || 0)
            },
          ]
        },
        {
          title: "chemicalParameterScreen.hardness",
          age: age,
          latestActualAge: lastActualChemical?.age || 0,
          isEmpty: isEmpty,
          data: [
            {
              key: "calcium",
              value: parseFloat(this.getValuePredictions(currentPrediction.predictions, 16).value || 0),
              actualValue: parseFloat(lastActualChemical?.calcium || 0)
            },
            {
              key: "magnesium",
              value: parseFloat(this.getValuePredictions(currentPrediction.predictions, 17).value || 0),
              actualValue: parseFloat(lastActualChemical?.magnesium || 0)
            },
            {
              key: "hardness",
              value: parseFloat(this.getValuePredictions(currentPrediction.predictions, 15).value || 0),
              actualValue: parseFloat(lastActualChemical?.hardness || 0)
            },
          ]
        },
        {
          title: "chemicalParameterScreen.otherParameter",
          age: age,
          latestActualAge: lastActualChemical?.age || 0,
          isEmpty: isEmpty,
          data: [
            {
              key: "ammonia",
              value: parseFloat(this.getValuePredictions(currentPrediction.predictions, 11).value || 0),
              actualValue: parseFloat(lastActualChemical?.ammonia || 0)
            },
            {
              key: "nitrat",
              value: parseFloat(this.getValuePredictions(currentPrediction.predictions, 12).value || 0),
              actualValue: parseFloat(lastActualChemical?.nitrat || 0)
            },
            {
              key: "nitrit",
              value: parseFloat(this.getValuePredictions(currentPrediction.predictions, 13).value || 0),
              actualValue: parseFloat(lastActualChemical?.nitrit || 0)
            },
            {
              key: "tom",
              value: parseFloat(this.getValuePredictions(currentPrediction.predictions, 20).value || 0),
              actualValue: parseFloat(lastActualChemical?.tom || 0)
            }
          ]
        }
      ]

      this.set("listParameterPredictions", models)
      return models;
    },
    async getSyncData(listActualChemical: Array<any>, sortType: "desc"|"asc") {
      const models = self.data.map(model => {
        const actualData = listActualChemical.find(value => value.measured_date === model.date)
        return {
          ...model,
          actualData
        }
      })
      if (sortType === "asc") {
        models.sort((a, b) => {
          return moment(a.date || a.measured_at).diff(moment(b.date || b.measured_at))
        })
      } else if (sortType === "desc") {
        models.sort((a, b) => {
          return moment(b.date || b.measured_at).diff(moment(a.date || a.measured_at))
        })
      }
      this.set("listSyncActualPredictions", models)
      return listActualChemical
    }
  })) // eslint-disable-line @typescript-eslint/no-unused-vars

type ChemicalPredictionsStoreType = Instance<typeof ChemicalPredictionsStoreModel>
export interface ChemicalPredictionsStore extends ChemicalPredictionsStoreType {}
type ChemicalPredictionsStoreSnapshotType = SnapshotOut<typeof ChemicalPredictionsStoreModel>
export interface ChemicalPredictionsStoreSnapshot extends ChemicalPredictionsStoreSnapshotType {}
export const createChemicalPredictionsStoreDefaultModel = () => types.optional(ChemicalPredictionsStoreModel, {})
